import { z } from 'zod';
import { message } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import type { Scope } from '@govard/tenant-core';
import type { ChatContextRef } from '@govard/ai-chat-kit';
import {
  uiCommandSchema,
  commandContextRefCount,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import { i18n } from '../../../i18n/index.js';

const refSchema = z
  .object({
    type: z.enum([
      'assistant_message',
      'crm_list',
      'deal',
      'contact',
      'company',
      'task',
      'note',
      'document',
      'calendar_event',
      'attachment',
      'template',
      'contact_interaction',
      'workspace_member',
      'user',
    ]),
    id: z.string().trim().min(1).max(200),
    label: z.string().max(12000),
  })
  .strict();
export const chatCommandHandoffSchema = z
  .object({
    version: z.literal(1),
    handoffId: z.string().uuid(),
    command: uiCommandSchema,
    contextRefs: z.array(refSchema).max(8).optional(),
  })
  .strict()
  .superRefine((handoff, context) => {
    if (commandContextRefCount(handoff.command, handoff.contextRefs ?? []) > 8)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contextRefs'],
        message: 'context_ref_limit',
      });
  });
export type ChatCommandHandoff = z.infer<typeof chatCommandHandoffSchema>;

/** External actions carry IDs only; the chat uses its existing durable send path. */
export function openChatCommand(
  navigate: NavigateFunction,
  command: UiCommandRequest,
  contextRefs?: ChatContextRef[],
): boolean {
  const parsed = chatCommandHandoffSchema.safeParse({
    version: 1,
    handoffId: crypto.randomUUID(),
    command,
    ...(contextRefs ? { contextRefs } : {}),
  });
  if (!parsed.success) {
    showInvalidHandoff(parsed.error);
    return false;
  }
  navigate('/chat', { state: { chatCommandHandoff: parsed.data } });
  return true;
}

export function showInvalidHandoff(error: z.ZodError): void {
  const limit = error.issues.some(
    (issue) => issue.message === 'context_ref_limit' || issue.code === 'too_big',
  );
  void message.info(
    i18n.t(limit ? 'crm.ai_chat.context_chips.max_reached' : 'crm.ai_chat.handoff.invalid_command'),
  );
}

const consumedInMemory = new Set<string>();
/** A scoped receipt also protects history restored by Back or a page reload. */
export function consumeChatCommandHandoff(
  scope: Pick<Scope, 'workspaceId' | 'userId'>,
  handoffId: string,
): boolean {
  const key = `govard.crm.chat-command-handoff.v1:${scope.workspaceId}:${scope.userId}:${handoffId}`;
  if (consumedInMemory.has(key)) return false;
  try {
    if (sessionStorage.getItem(key) === 'consumed') return false;
    sessionStorage.setItem(key, 'consumed');
  } catch {
    // Private browsing still gets the in-memory consume-once guard.
  }
  consumedInMemory.add(key);
  return true;
}
